import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { Loader2, X } from 'lucide-react';
import { BoxOverlay, boxZoomStyle, type DrawnBox } from './BoxOverlay';
import { CropEditor } from './CropEditor';
import { useLanguage } from '../../contexts/LanguageContext';
import { modalBackdropCls, modalCardCls, modalCloseCls, zoomOverlayCls } from './overlay';
import { AboveLayer, LAYER_STEP, useLayer } from './overlayLayer';
import { StepArrows, useStepThrough } from './stepThrough';
import type { HeroBox } from './realImages';

// A full-screen image zoom overlay: click the backdrop, the close button, or
// press Escape to dismiss. Shared by the parts table (PIÈCE crops) and Phase 2's
// candidate web images, so every Notice image zooms the same way. Renders
// nothing when `src` is null.
export function ImageLightbox({
  src,
  alt,
  srcs,
  onNavigate,
  stepIds,
  stepId,
  box,
  boxes,
  waiting,
  view,
  zoomTo,
  onToggleBox,
  onBoxChange,
  details,
  corner,
  onClose,
}: {
  src: string | null;
  alt?: string;
  // The set this zoom belongs to, so one can be compared with the next without
  // closing and reopening — which is the only way to compare pictures at all, since
  // you cannot hold two of them in your head at thumbnail size. Omit for a lone
  // picture: the arrows and the arrow keys then have nothing to step through and are
  // not rendered.
  srcs?: string[];
  onNavigate?: (src: string) => void;
  // WHAT TO STEP BY, when the pictures are not what distinguishes them. Two crops of one
  // part can come out of the same photograph, so a URL names neither — these give the set
  // and the open one their own identity, and onNavigate is called with it. Omitted, the
  // srcs are the identity, which is true of a gallery.
  stepIds?: string[];
  stepId?: string;
  // The crop being EDITED, for the CropEditor path below. Drawn boxes go in `boxes`.
  box?: HeroBox | null;
  // The rectangles to draw on THIS picture: none, one, or one per instance of a part that
  // is in the photograph several times. The caller decides which — stepping through a
  // gallery, only one of the pictures is the splash, and a box belongs to the photo it was
  // drawn on.
  boxes?: DrawnBox[];
  // THIS PICTURE'S ANSWER IS STILL BEING READ. Dimmed, with a spinner on it — the same two
  // marks the thumbnail wears, because they mean the same thing and the zoom is the same
  // picture bigger.
  //
  // Without it a photograph that has not been looked at yet opens as a clean, boxless
  // product shot, which is precisely how a photograph the model found NOTHING in opens. Two
  // states, one appearance, and the difference between them is the whole answer.
  waiting?: boolean;
  // OPEN ON THE BOX, not on the picture with a box drawn on it. For a tile that IS a crop:
  // what was clicked is a wood button, and opening the chair it was cut from answers a
  // question nobody asked. The picture is moved and scaled inside its own frame — the same
  // arithmetic the tile used — so the thing that grew is the thing that was pressed.
  zoomTo?: HeroBox | null;
  // Clicking a rectangle strikes it through, and clicking it again brings it back. The
  // gesture that used to zoom into the box: a second magnification answered a question the
  // whole picture had already answered, where "that one is wrong" had nowhere to be said.
  onToggleBox?: (index: number) => void;
  // When given, the crop is EDITABLE here (drag the corners) instead of drawn static —
  // the zoom is where a splash is cropped now, there being no separate cover editor.
  // Provided only for the picture that owns the crop; omit for a look-only zoom.
  onBoxChange?: (box: HeroBox) => void;
  // Provenance to show under the picture — what it was made from and what was asked of
  // it. Its PRESENCE changes the zoom: the picture is framed in a panel with this beneath
  // it, and given less of the screen, because it is no longer the only thing being read.
  // Omit for a plain look-at-it zoom and nothing changes.
  details?: ReactNode;
  // WHICH SIDE OF THE PRODUCT THIS PICTURE SHOWS, in words — "Back", "Left front 45°" —
  // where the caller knows. Phase 1 classifies every listing photograph and the answer is
  // read by four other things (the orientation call, which pictures the estimate buys, the
  // order of the context row); the one place it was never said is the place the picture is
  // big enough to disagree with it. Omitted where nothing classified the picture, which is
  // not the same as "no view": see OTHER_DIRECTION.
  view?: string;
  // A CONTROL IN THE ZOOM'S OWN TOP-LEFT, opposite the close. For what acts on the picture
  // as a THING — the Parts-DB switch, which is on every tile in the strip and was missing
  // from the one place the picture is big enough to judge. Deciding whether a render is
  // good enough to keep is exactly what the zoom is opened for, and the answer had to be
  // given back on a 80-pixel tile.
  corner?: ReactNode;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const z = useLayer() + LAYER_STEP;

  // ← and → walk the gallery, wrapping; the arrow keys are claimed while it can step.
  const { at, total, canStep, step } = useStepThrough(stepIds ?? srcs, stepId ?? src, onNavigate);

  useEffect(() => {
    if (!src) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [src, onClose]);

  if (!src) return null;
  const drawn = boxes ?? [];

  // How far through the set, in one pill, under the picture.
  const counter = (
    // WHAT THE PICTURE IS OF, beside how far through the set it is — the two facts that
    // place a photograph, in one pill. In the same pill and not a second one: "3 / 8" and
    // "Back" are one sentence about the picture on screen, and a caller that has no
    // direction to give simply says the fraction, as it always did.
    <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
      <span className="tabular-nums">{at + 1} / {total}</span>
      {view && (
        <>
          <span className="text-slate-500">·</span>
          {/* Lighter than the count, which is the thing that changes as you step: the
              direction is a label on the picture, not a position in the set. */}
          <span className="font-medium text-slate-400">{view}</span>
        </>
      )}
    </span>
  );

  // THE PICTURE ITSELF, one definition for both layouts — bare on the backdrop, or matted
  // inside a panel with its provenance under it. They differ in how much room the image
  // gets and in nothing else.
  //
  // The wrapper shrink-wraps the picture, so it IS the picture: the boxes are positioned
  // against the pixels rather than against the window. Hence `w-auto` and no
  // `object-contain` in either caller — a letterboxed image would put every box off by the
  // size of the letterbox.
  const picture = (imgCls: string) => (
    <span
      className={`relative inline-block ${zoomTo ? 'overflow-hidden rounded-lg' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={src}
        alt={alt}
        style={zoomTo ? boxZoomStyle(zoomTo) : undefined}
        className={`${imgCls} cursor-default`}
      />
      {/* A SCRIM OVER IT, not opacity on it. Fading the picture makes the picture
          see-through, and what is behind it here is a black backdrop with the whole parts
          table under that — so a photograph waiting for its answer showed the page through
          the chair. On a tile the same fade is fine, because a tile has white behind it.

          The veil is the colour the pictures already sit on, so what it reads as is a
          washed-out photograph rather than a hole. */}
      {waiting && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-white/70">
          <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
        </span>
      )}
      {/* Not while zoomed into one: a rectangle around everything on screen says nothing,
          and the boxes of other instances would be off the edge of it.

          NO VEIL, here either. It was drawn when a picture held exactly one box — the veil
          is that box's own shadow and two of them cancel — so the same answer arrived
          darkened or bright depending on how many instances the model happened to find,
          and the thumbnail you clicked never matched the picture it opened.

          The photograph is the evidence and dimming it hides the thing being checked: a
          box on a chair leg is right or wrong because of the leg around it. The rectangle
          is legible on its own. (CropEditor keeps the veil: choosing a cover crop is about
          what falls OUTSIDE the frame, which is the one case the dim is about.) */}
      {!zoomTo && drawn.map((b, i) => (b.hidden ? null : (
        <BoxOverlay
          key={i}
          box={b.box}
          outlines={b.outlines}
          label={b.label}
          tone={b.tone}
          across={b.across}
          up={b.up}
          off={b.off}
          frame={b.frame}
          veil={false}
          onClick={onToggleBox && (() => onToggleBox(i))}
          title={onToggleBox ? t('notice.parts.toggleCrop') : alt}
        />
      )))}
    </span>
  );

  // OUT OF WHATEVER OPENED IT, and into the body.
  //
  // A zoom is about something already on screen, so the thing it was opened from is always
  // underneath it — and a dialog is a stacking context, so a lightbox rendered inside one
  // cannot paint above that dialog's siblings however high its z-index goes. Clicking a
  // render in the orientation prompt's preview opened the picture BEHIND the panel it was
  // clicked in, which is the whole of what this fixes. See zoomOverlayCls for the other
  // half.
  //
  // Events still reach the callers that expect them: a portal moves the DOM node and not
  // the React tree, so a click inside here bubbles to the same handlers it always did.
  // Above whatever opened it, and the layer everything inside it opens from. A badge in
  // the corner of a zoom puts its menu over the zoom, not under the backdrop it sits on.
  return createPortal((
    <AboveLayer z={z}>
    <div
      onClick={onClose}
      style={{ zIndex: z }}
      className={`${zoomOverlayCls} cursor-zoom-out ${modalBackdropCls}`}
    >
      {/* ONE CARD FOR EVERYTHING (see modalCardCls): the picture, its corner badge, the close
          button, the step arrows, where in the set it is and — when there is any — what it
          was made from, under it. They floated on the black around a bare picture; the
          card is the look every dialog in the app has.

          The card swallows the click that would close the backdrop, so a press on the
          corner's switch toggles it rather than dismissing the picture it is about. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative flex max-h-[92vh] cursor-default flex-col items-center overflow-y-auto pb-4 pt-12 ${
          canStep ? 'px-16' : 'px-12'
        } ${details ? 'w-full max-w-4xl' : 'max-w-[94vw]'} ${modalCardCls}`}
      >
        {corner && <div className="absolute left-3 top-3 z-10">{corner}</div>}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('notice.pdf.close')}
          className={modalCloseCls}
        >
          <X className="h-5 w-5" />
        </button>
        {canStep && <StepArrows onStep={step} />}

        {details ? (
          // A MATTE ROUND THE PICTURE, so the panel reads as a picture in a card rather
          // than a card that starts with an image. It also gives a photograph shot on
          // white somewhere to end: without it the product bleeds into the panel and the
          // boxes look drawn on the page. The picture gets less room than bare, because
          // what it was made from has to be readable under it rather than past the fold.
          <div className="flex w-full justify-center rounded-xl bg-slate-100 p-3">
            {picture('block max-h-[70vh] w-auto max-w-full rounded-lg bg-white')}
          </div>
        ) : onBoxChange ? (
          // Editable: drag the corners to crop, zoomed. This is where a splash is cropped.
          <span className="relative inline-block">
            <CropEditor
              src={src}
              box={box ?? null}
              onChange={onBoxChange}
              alt={alt}
              imgClassName="block max-h-[calc(92vh-7rem)] max-w-[calc(94vw-8rem)] cursor-default rounded-lg bg-white shadow-lg"
            />
          </span>
        ) : (
          picture('block max-h-[calc(92vh-7rem)] max-w-[calc(94vw-8rem)] rounded-lg bg-white shadow-lg')
        )}

        {/* WHERE IN THE SET, under the picture it counts. */}
        {canStep && <div className="mt-3 flex justify-center">{counter}</div>}
        {details && <div className="mt-4 w-full text-left">{details}</div>}
      </div>
    </div>
    </AboveLayer>
  ), document.body);
}
